/**
 * Turning a JSON:API error body into a thrown Error, without dropping the code.
 *
 * The code is the part callers act on: a 403 saying "you are not an admin" and
 * a 403 saying "this gym has not signed the agreement" need different screens,
 * and the human-readable detail is not something to branch on.
 */

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>
  /** The OAuth-shaped body devise-api answers with on the token endpoints. */
  error?: string
  error_description?: string | string[]
}

export class ApiCodedError extends Error {
  code?: string
  /**
   * Every code in the body, not only the first: a form that puts each refusal
   * under its own field (the entry dialog) wants all of them.
   */
  codes: string[]

  constructor(message: string, code?: string, codes?: string[]) {
    super(message)
    this.name = 'ApiCodedError'
    this.code = code
    this.codes = codes ?? (code ? [code] : [])
  }
}

export function errorCodeOf(error: unknown): string | undefined {
  return error instanceof ApiCodedError ? error.code : undefined
}

/** All the codes a refusal carried, in order; empty for anything else. */
export function errorCodesOf(error: unknown): string[] {
  return error instanceof ApiCodedError ? error.codes : []
}

export async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody
  // An endpoint that mints a session answers in devise-api's shape rather than
  // JSON:API's — the guided sign-up does both — so read either one.
  const described = Array.isArray(body.error_description)
    ? body.error_description.join(', ')
    : body.error_description
  const codes = (body.errors ?? []).map((error) => error.code).filter((code): code is string => !!code)
  throw new ApiCodedError(
    body.errors?.[0]?.detail || described || fallback,
    body.errors?.[0]?.code || body.error,
    codes.length > 0 ? codes : undefined
  )
}
